// Fax Cover Sheet Generator for Fax/Phone Submission Lane
import { jsPDF } from 'jspdf';
import { AuthorizationRequest } from '@/lib/authorization/types';
import { format } from 'date-fns';

interface FaxCoverOptions {
  toName: string;
  toFaxNumber: string;
  fromName: string;
  fromFaxNumber: string;
  fromPhone: string;
  subject: string;
  totalPages: number;
  urgent: boolean;
  message?: string;
}

/**
 * Generate a fax cover sheet PDF
 */
export function generateFaxCoverSheet(
  caseData: AuthorizationRequest,
  options: FaxCoverOptions
): jsPDF {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 20;
  
  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('FAX COVER SHEET', pageWidth / 2, y, { align: 'center' });
  y += 15;
  
  // Urgent indicator
  if (options.urgent) {
    doc.setFillColor(220, 38, 38);
    doc.rect(margin, y, pageWidth - margin * 2, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text('URGENT - PRIOR AUTHORIZATION REQUEST', pageWidth / 2, y + 8, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y += 20;
  }
  
  // Date and time
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${format(new Date(), 'MMMM d, yyyy')}`, margin, y);
  doc.text(`Time: ${format(new Date(), 'h:mm a')}`, pageWidth - margin - 50, y);
  y += 15;
  
  // Divider
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;
  
  // TO section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TO:', margin, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${options.toName}`, margin + 10, y);
  y += 6;
  doc.text(`Fax Number: ${options.toFaxNumber}`, margin + 10, y);
  y += 15;
  
  // FROM section
  doc.setFont('helvetica', 'bold');
  doc.text('FROM:', margin, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${options.fromName}`, margin + 10, y);
  y += 6;
  doc.text(`Fax Number: ${options.fromFaxNumber}`, margin + 10, y);
  y += 6;
  doc.text(`Phone: ${options.fromPhone}`, margin + 10, y);
  y += 15;
  
  // Divider
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;
  
  // Case information
  doc.setFont('helvetica', 'bold');
  doc.text('PRIOR AUTHORIZATION REQUEST', margin, y);
  y += 10;
  
  doc.setFont('helvetica', 'normal');
  const infoLines = [
    `Case Number: ${caseData.case_number}`,
    `Patient: ${caseData.patient ? `${caseData.patient.last_name}, ${caseData.patient.first_name}` : 'N/A'}`,
    `MRN: ${caseData.patient?.mrn || 'N/A'}`,
    `Member ID: ${caseData.patient?.member_id || 'N/A'}`,
    ``,
    `CPT Code(s): ${caseData.cpt_codes.join(', ')}`,
    `Modifier(s): ${caseData.modifiers?.join(', ') || 'None'}`,
    `ICD-10 Code(s): ${caseData.icd10_codes.join(', ')}`,
    ``,
    `Procedure: ${caseData.procedure_description}`,
    `Scheduled Date: ${caseData.scheduled_date ? format(new Date(caseData.scheduled_date), 'MMMM d, yyyy') : 'TBD'}`,
    `Urgency: ${caseData.urgency?.toUpperCase() || 'ROUTINE'}`,
  ];
  
  infoLines.forEach(line => {
    doc.text(line, margin + 10, y);
    y += 6;
  });
  
  y += 10;
  
  // Divider
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;
  
  // Page count
  doc.setFont('helvetica', 'bold');
  doc.text(`TOTAL PAGES (including this cover): ${options.totalPages}`, margin, y);
  y += 15;
  
  // Message section
  if (options.message) {
    doc.setFont('helvetica', 'bold');
    doc.text('MESSAGE:', margin, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(options.message, pageWidth - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 6 + 10;
  }
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 30;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text(
    'This fax contains confidential patient health information protected under HIPAA.',
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );
  doc.text(
    'If you received this in error, please destroy immediately and notify the sender.',
    pageWidth / 2,
    footerY + 5,
    { align: 'center' }
  );
  
  // Generated by LINK
  doc.setFontSize(8);
  doc.text(
    `Generated by LINK Ops • ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
    pageWidth / 2,
    footerY + 15,
    { align: 'center' }
  );
  
  return doc;
}

/**
 * Generate filename for fax cover sheet
 */
export function generateFaxCoverFilename(caseNumber: string): string {
  const timestamp = format(new Date(), 'yyyyMMdd-HHmmss');
  return `FaxCover_${caseNumber}_${timestamp}.pdf`;
}
